import { Award, FileText, Target, CheckCircle2 } from 'lucide-react';
import Card from '../ui/Card';
import { MODULE_LABELS, type Report } from '../../mentorshipService';
import './ReportViewer.css';

type Props = {
    report: Report;
};

function formatTimestamp(iso: string): string {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return iso;
    }
}

function ReportViewer({ report }: Props) {
    const moduleLabel =
        MODULE_LABELS[report.submission_summary.module] ??
        report.submission_summary.module;

    const levelChipClass = `mentorship-chip mentorship-chip--level-${report.agreed_level.toLowerCase()}`;

    return (
        <article className="report-viewer">
            <header className="report-viewer__header">
                <div className="report-viewer__header-meta">
                    <span className="mentorship-chip mentorship-chip--module">{moduleLabel}</span>
                    <span className={levelChipClass}>{report.agreed_level}</span>
                    <span className="report-viewer__version">v{report.version}</span>
                </div>
                <h2 className="report-viewer__title">
                    Quarterly review · {report.submission_summary.quarter}
                </h2>
                <p className="report-viewer__date">
                    Finalized {formatTimestamp(report.timestamp)}
                </p>
            </header>

            <Card variant="row" className="report-viewer__score-card">
                <Card.Header>
                    <Award size={32} />
                    <div>
                        <p className="report-viewer__score-label">Final score</p>
                        <p className="report-viewer__score-value">{report.final_score} / 5</p>
                    </div>
                </Card.Header>
            </Card>

            {report.strengths.length > 0 && (
                <section className="report-viewer__section">
                    <h3 className="report-viewer__section-title">
                        <CheckCircle2 size={18} /> Strengths
                    </h3>
                    <ul className="report-viewer__list">
                        {report.strengths.map((s, i) => (
                            <li key={i}>{s}</li>
                        ))}
                    </ul>
                </section>
            )}

            {report.gaps.length > 0 && (
                <section className="report-viewer__section">
                    <h3 className="report-viewer__section-title">
                        <Target size={18} /> Gaps
                    </h3>
                    <ul className="report-viewer__list">
                        {report.gaps.map((g, i) => (
                            <li key={i}>{g}</li>
                        ))}
                    </ul>
                </section>
            )}

            {report.actions.length > 0 && (
                <section className="report-viewer__section">
                    <h3 className="report-viewer__section-title">
                        <FileText size={18} /> Development actions
                    </h3>
                    <ul className="report-viewer__list">
                        {report.actions.map((a, i) => (
                            <li key={i}>{a}</li>
                        ))}
                    </ul>
                </section>
            )}

            {report.mentor_notes && (
                <section className="report-viewer__section">
                    <h3 className="report-viewer__section-title">Mentor notes</h3>
                    <p className="report-viewer__notes">{report.mentor_notes}</p>
                </section>
            )}

            {report.benchmark_tag && (
                <p className="report-viewer__benchmark">
                    Benchmark tag: <code>{report.benchmark_tag}</code>
                </p>
            )}
        </article>
    );
}

export default ReportViewer;
